import { useEffect } from 'react';
import * as Sentry from '@sentry/react';

import type { PatientOrderingPaymentIntentRequest } from '@customTypes/test-ordering';
import useToast from '@hooks/useToast';
import { createOrUpdatePaymentIntent } from '@store/actions/test-ordering';
import { isMinorDOB } from '@utils/dates';

import { OrderActions } from '../types/Order.actions';
import { useOrderContext } from './useOrderContext';

interface useFetchPaymentIntentProps {
  promoCode: string | undefined;
}

export const useFetchPaymentIntent = (props: useFetchPaymentIntentProps) => {
  const promoCode = props.promoCode;

  const { addToast } = useToast();

  const { dispatch, state, requiresPaymentStep } = useOrderContext();
  const { campaign_code: campaignCode, guardian, paymentIntent, subject } = state;

  useEffect(() => {
    let isMounted = true;

    const subjectContactInfo = subject.contact_info[0];
    const emailAddress = subjectContactInfo?.email || guardian?.email;

    if (!requiresPaymentStep || !emailAddress) {
      return;
    }

    const isMinorSubject = isMinorDOB(subject.demographics.dob);

    const payload: PatientOrderingPaymentIntentRequest = {
      campaign_code: campaignCode ?? null,
      payment_intent_id: paymentIntent?.id ?? null,
      promo_code: promoCode ?? null,
      subject: {
        first_name: subject.name.first || '',
        last_name: subject.name.last || '',
        dob: subject.demographics.dob || '',
        sex: subject.demographics.sex || '',
        email: subjectContactInfo?.email || null,
        phone: subjectContactInfo?.phone || null,
      },
    };

    if (isMinorSubject && guardian) {
      payload.guardian = {
        first_name: guardian.first_name,
        last_name: guardian.last_name,
        dob: guardian.dob,
        email: guardian.email,
        phone: guardian.phone,
        relationship: guardian.relationship,
      };
    }

    const fetchPaymentIntent = async () => {
      try {
        const response = await createOrUpdatePaymentIntent(payload);

        if (isMounted) {
          dispatch({
            type: OrderActions.PAYMENT_INTENT_CREATED,
            value: response.payment_intent,
          });
        }
      } catch (error) {
        // Log error to Sentry with context
        Sentry.captureException(error, {
          tags: {
            component: 'useFetchPaymentIntent',
            action: 'initPaymentIntent',
          },
          extra: {
            campaignCode,
            promoCode,
            hasEmail: !!emailAddress,
            errorMessage:
              error instanceof Error
                ? error.message
                : error && typeof error === 'object' && 'message' in error
                  ? String(error.message)
                  : 'Unknown error',
          },
        });

        // Show user-friendly error message
        if (isMounted) {
          addToast({
            variant: 'warning',
            headerTitle: 'Product Information Unavailable',
            content: 'We were unable to load product information. You may need to refresh the page.',
            autohide: false,
          });
        }
      }
    };

    fetchPaymentIntent();

    return () => {
      isMounted = false;
    };

    // Intentionally runs once on mount only - values are captured at mount time
    // Assumes subjectEmail and requiresPaymentStep are ready when PaymentStep renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    paymentIntentId: paymentIntent?.id,
  };
};
